import {
  Title,
  Subtitle,
  Input,
  SubmitButton,
  Typography,
  Popover,
  EyeButton,
} from '~components'
import { object, string, ref } from '~utils/validation'
import { Auth } from '~services'
import * as Styled from './styled'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { useRequest } from 'ahooks'
import { useStore, usePathValidation, useNavigate } from '~hooks'
import { useSearchParams } from 'react-router-dom'
import { Values } from './interfaces'
import { track } from '@amplitude/analytics-browser'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

function ResetPassword() {
  const schema = object({
    newPassword: string()
      .required()
      .min(8, 'Pelo menos 8 caracteres')
      .max(72, 'No máximo 72 caracteres')
      .atLeastOneCharacter('Pelo menos uma letra')
      .atLeastOneNumber('Pelo menos um número'),
    newPasswordConfirmation: string()
      .required()
      .oneOf([ref('newPassword'), null], 'As senhas devem ser iguais'),
  })
  const defaultValues = {
    newPassword: '',
    newPasswordConfirmation: '',
  }
  const [searchParams] = useSearchParams()
  const hasTokens =
    searchParams.get('user_uuid') && searchParams.get('password_recovery_uuid')
  const { cpf, update } = useStore()
  const navigate = useNavigate()
  const { runAsync: changePassword, loading } = useRequest(Auth.changePassword, {
    manual: true,
  })
  const { runAsync: verifyResetPasswordCodeWhatsapp } = useRequest(
    Auth.verifyResetPasswordCodeWhatsapp,
    {
      manual: true,
    },
  )
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showNewPasswordConfirmation, setShowNewPasswordConfirmation] = useState(false)
  const [isPopoverVisible, setPopoverVisible] = useState(false)
  const {
    handleSubmit,
    formState: { errors, isValid, dirtyFields },
    register,
    setFocus,
  } = useForm({
    defaultValues,
    mode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(schema),
  })
  const { executeRecaptcha } = useGoogleReCaptcha()

  const atLeastEightCharacters = !errors?.newPassword?.types?.min
  const atLeastOneNumber = !errors?.newPassword?.types?.atLeastOneNumber
  const atLeastOneCharacter = !errors?.newPassword?.types?.atLeastOneCharacter
  const newPasswordMessages = [
    { text: 'Pelo menos 8 caracteres', isSuccess: atLeastEightCharacters },
    { text: 'Pelo menos uma letra', isSuccess: atLeastOneCharacter },
    { text: 'Pelo menos um número', isSuccess: atLeastOneNumber },
  ]

  usePathValidation(hasTokens ? [] : ['cpf'])
  useEffect(() => {
    if (hasTokens) {
      verifyResetPasswordCodeWhatsapp(
        searchParams.get('user_uuid'),
        searchParams.get('password_recovery_uuid'),
      )
        .then((data) => {
          localStorage.setItem('confirmation_token', data.confirmation_token)
          update({ cpf: data.username })
        })
        .catch(() =>
          navigate('/recuperar-senha-motorista', {
            state: { previousPath: '/recuperar-senha-motorista/redefinir' },
          }),
        )
    } else {
      const confirmation_token = localStorage.getItem('confirmation_token')
      if (!confirmation_token) navigate('/recuperar-senha-motorista')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleFocus() {
    setPopoverVisible(true)
  }
  function handleBlur() {
    setPopoverVisible(false)
  }
  function handleClickNewPasswordView() {
    setFocus('newPassword')
    setShowNewPassword((showNewPassword) => !showNewPassword)
  }
  function handleClickNewPasswordConfirmationView() {
    setFocus('newPasswordConfirmation')
    setShowNewPasswordConfirmation(
      (showNewPasswordConfirmation) => !showNewPasswordConfirmation,
    )
  }
  function handleChange(event: { target: { value: string } }) {
    const value = event.target.value
    if (value.includes(' ')) event.target.value = value.replace(/ /g, '')
  }

  function handlePaste(event: { preventDefault: () => void }) {
    event.preventDefault()
  }
  async function onSubmit(values: Values) {
    const confirmation_token = localStorage.getItem('confirmation_token')
    const recaptchaToken = await executeRecaptcha('action')

    try {
      await changePassword(values.newPassword, confirmation_token, cpf, recaptchaToken)
      track('ignito__recuperacao_de_senha__nova_senha__sucesso')
      navigate('/recuperar-senha-motorista/sucesso')
    } catch {
      track('ignito__recuperacao_de_senha__nova_senha__falha')
      navigate('/erro/envio-dados')
    }
  }

  return (
    <form aria-labelledby="reset-password-title" onSubmit={handleSubmit(onSubmit)}>
      <Title id="reset-password-title">Crie uma nova senha</Title>
      <Subtitle>
        <Typography as="b">Atençāo!</Typography> A senha deve ter pelo menos 8 caracteres,
        usando letras e números.
      </Subtitle>
      <Typography as="label">Nova senha</Typography>
      <Styled.WrapperRelative>
        {dirtyFields?.newPassword && isPopoverVisible && (
          <Popover title="Sua senha deve conter:" messages={newPasswordMessages} />
        )}
        <Input
          onPaste={handlePaste}
          onChange={handleChange}
          type={showNewPassword ? 'text' : 'password'}
          onFocus={handleFocus}
          onBlur={handleBlur}
          register={register}
          name="newPassword"
          placeholder=" "
          data-testid="newPassword"
          maxLength={72}
        />
        <EyeButton
          data-testid="toggle-new-password-visibility"
          onClick={handleClickNewPasswordView}
          visibility={showNewPassword}
        />
      </Styled.WrapperRelative>
      <Typography as="label">Repita a nova senha</Typography>
      <Styled.WrapperRelative>
        <Input
          type={showNewPasswordConfirmation ? 'text' : 'password'}
          register={register}
          name="newPasswordConfirmation"
          placeholder=" "
          data-testid="newPasswordConfirmation"
        />
        {errors?.newPasswordConfirmation && (
          <Popover
            title="Atenção"
            messages={[{ text: 'As senhas devem ser iguais', isSuccess: false }]}
          />
        )}
        <EyeButton
          data-testid="toggle-confirm-password-visibility"
          onClick={handleClickNewPasswordConfirmationView}
          visibility={showNewPasswordConfirmation}
        />
      </Styled.WrapperRelative>
      <SubmitButton width="100%" disabled={!isValid} loading={loading}>
        Salvar nova senha
      </SubmitButton>
    </form>
  )
}

export default ResetPassword
